import { COPY } from '../copy/vi.ts';
import type { FlowState } from './useCheckinFlow.ts';

/**
 * The in-form part of the submit state machine (§11).
 *
 * M05 and M06 get their own screen (PendingScreen); what stays on the form is
 * the short-lived M04 status and the two errors the participant has to read
 * with their answers still in front of them.
 */
export function SubmissionStatus({ state }: { state: FlowState }) {
  if (state.kind === 'submitting') {
    return (
      <p
        role="status"
        aria-live="polite"
        className="rounded-control bg-brand/8 px-4 py-3 text-center text-sm font-semibold text-brand"
      >
        {/* §13 — "verifying" must never be worded as success. */}
        {state.phase === 'sending' ? COPY.sending.title : COPY.sending.verifying}
      </p>
    );
  }

  if (state.kind === 'rejected' || state.kind === 'save_failed') {
    return (
      <p
        role="alert"
        className="rounded-control border-[1.5px] border-danger bg-danger-soft px-4 py-3 text-sm font-semibold text-danger"
      >
        <span aria-hidden="true">⚠ </span>
        {state.kind === 'rejected' ? state.message : COPY.saveFailed.body}
      </p>
    );
  }

  return null;
}

/**
 * L-003 / §18 — a pending record found at startup.
 *
 * Non-blocking on purpose: the participant may be here to check in again on a
 * shared phone, so the form stays usable while the old record is retried.
 */
export function RecoveryBanner({
  pendingCount,
  retrying,
  onRetry,
}: {
  pendingCount: number;
  retrying: boolean;
  onRetry: () => void;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-col gap-3 rounded-control border-[1.5px] border-warning bg-warning-soft px-4 py-4"
    >
      <p className="text-sm font-semibold text-warning">
        {pendingCount > 1
          ? `${COPY.recovery.manyPrefix} ${String(pendingCount)}`
          : COPY.recovery.single}
      </p>
      <button
        type="button"
        onClick={onRetry}
        disabled={retrying}
        className="h-12 rounded-control bg-warning text-sm font-extrabold tracking-wide text-white disabled:opacity-60"
      >
        {retrying ? COPY.sending.verifying : COPY.recovery.action}
      </button>
    </div>
  );
}

/**
 * K-002 — a new bundle is installed and waiting.
 *
 * Shown only when nothing is in flight (see CheckinExperience): reloading
 * mid-submission would turn a finishing check-in into a recovery case.
 */
export function UpdatePrompt({ onApply }: { onApply: () => void }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border bg-surface px-5 py-3">
      <p className="text-sm text-text-muted">{COPY.update.body}</p>
      <button
        type="button"
        onClick={onApply}
        className="h-10 rounded-control border-[1.5px] border-brand px-4 text-sm font-bold text-brand"
      >
        {COPY.update.action}
      </button>
    </div>
  );
}
